import React, { useRef, useState } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import ImageGenerationModal from './ImageGenerationModal'
import ImageLibraryModal from '../ImageLibrary/ImageLibraryModal'
import { useAuth } from '../../contexts/AuthContext'
import { imageLibraryService } from '../../lib/imageLibraryService'

const { FiSettings, FiType, FiImage, FiVideo, FiEdit3, FiUpload, FiCopy, FiSquare, FiCircle, FiTriangle, FiFolder } = FiIcons

const PropertiesPanel = ({ slide, selectedElement, onUpdateSlide, onUpdateElement, onApplyBackgroundToAll, projectTitle, genre }) => {
  const { user } = useAuth()
  const fileInputRef = useRef(null)
  const backgroundFileInputRef = useRef(null)
  const [showImageGenerationModal, setShowImageGenerationModal] = useState(false)
  const [showImageLibrary, setShowImageLibrary] = useState(false)
  const [showBackgroundImageLibrary, setShowBackgroundImageLibrary] = useState(false)
  
  const element = selectedElement && selectedElement !== 'background'
    ? slide.elements.find(el => el.id === selectedElement)
    : null
  
  const isBackgroundSelected = selectedElement === 'background'

  const handleElementUpdate = (updates) => {
    if (element) {
      onUpdateElement(element.id, updates)
    }
  }

  const handleStyleUpdate = (styleUpdates) => {
    if (element) {
      onUpdateElement(element.id, {
        style: { ...element.style, ...styleUpdates }
      })
    }
  }

  const handleBackgroundChange = (type, value, options = {}) => {
    if (type === 'image') {
      onUpdateSlide({
        background: { type, url: value, ...options }
      })
    } else {
      onUpdateSlide({
        background: { type, value, ...options }
      })
    }
  }

  const handleImageUpload = async (e) => {
    const file = e.target.files[0]
    if (!file || !user) return

    // For quick upload, we'll use the traditional file reader for immediate preview
    const reader = new FileReader()
    reader.onload = (e) => {
      handleElementUpdate({ src: e.target.result })
    }
    reader.readAsDataURL(file)

    // Also save to library in background (don't await to keep it quick)
    setTimeout(async () => {
      try {
        await imageLibraryService.uploadImage(user.id, file, {
          tags: ['quick-upload'],
          description: 'Quick upload from slide editor',
          altText: file.name
        })
        console.log('📚 Quick upload saved to library')
      } catch (error) {
        console.warn('⚠️ Failed to save quick upload to library:', error)
      }
    }, 100)
  }

  const handleBackgroundImageUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        handleBackgroundChange('image', null, {
          url: e.target.result,
          position: 'center',
          size: 'cover'
        })
      }
      reader.readAsDataURL(file)
    }
  }

  const handleGeneratedImage = (generatedImage) => {
    if (element && element.type === 'image') {
      handleElementUpdate({ 
        src: generatedImage.url,
        generatedImageId: generatedImage.id
      })
    }
  }

  const handleImageSelected = (selectedImage) => {
    if (element && element.type === 'image') {
      handleElementUpdate({ 
        src: selectedImage.url,
        libraryImageId: selectedImage.id
      })
    }
  }

  const handleBackgroundImageSelected = (selectedImage) => {
    handleBackgroundChange('image', null, {
      url: selectedImage.url,
      position: 'center',
      size: 'cover'
    })
    setShowBackgroundImageLibrary(false)
  }

  const getImageElementType = () => {
    if (!element) return 'custom'
    
    // Determine image type based on slide type or position
    if (slide.type === 'cover' || slide.title?.toLowerCase().includes('cover')) {
      return 'cover'
    } else if (slide.title?.toLowerCase().includes('character')) {
      return 'character'
    } else {
      return 'scene'
    }
  }

  const parseGradient = (gradientString) => {
    // Simple regex to extract colors from linear-gradient
    const matches = gradientString.match(/#[a-fA-F0-9]{6}/g)
    if (matches && matches.length >= 2) {
      return { color1: matches[0], color2: matches[1] }
    }
    return { color1: '#667eea', color2: '#764ba2' }
  }

  const updateGradient = (color1, color2) => {
    const gradient = `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`
    handleBackgroundChange('gradient', gradient)
  }

  const shapeOptions = [
    { id: 'rectangle', name: 'Rectangle', icon: FiSquare },
    { id: 'circle', name: 'Circle', icon: FiCircle },
    { id: 'triangle', name: 'Triangle', icon: FiTriangle }
  ]

  const handleShapeChange = (shapeType) => {
    const shapeStyles = {
      rectangle: { borderRadius: '0px' },
      circle: { borderRadius: '50%' },
      triangle: { 
        borderRadius: '0px',
        clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
        backgroundColor: element.style.backgroundColor || '#3b82f6'
      }
    }

    handleStyleUpdate({
      shapeType,
      ...shapeStyles[shapeType]
    })
  }

  return (
    <>
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-200">
        <h3 className="font-medium text-gray-900 flex items-center space-x-2">
          <SafeIcon icon={isBackgroundSelected ? FiEdit3 : FiSettings} className="w-4 h-4" />
          <span>{isBackgroundSelected ? 'Background Properties' : selectedElement ? 'Element Properties' : 'Properties'}</span>
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {!selectedElement && (
          <div className="text-center py-8 text-gray-500">
            <SafeIcon icon={FiSettings} className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Select an element or background to edit properties</p>
          </div>
        )}
        
        {isBackgroundSelected && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-900">Slide Background</h4>
              <button
                onClick={onApplyBackgroundToAll}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center space-x-1"
              >
                <SafeIcon icon={FiCopy} className="w-3 h-3" />
                <span>Apply to All</span>
              </button>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Background Color</label>
                <input
                  type="color"
                  value={slide?.background?.type === 'color' ? slide.background.value : '#ffffff'}
                  onChange={(e) => handleBackgroundChange('color', e.target.value)}
                  className="w-full h-8 rounded border border-gray-300"
                />
              </div>
              
              <div>
                <label className="block text-xs text-gray-600 mb-1">Gradient</label>
                <select
                  value={slide.background?.type === 'gradient' ? 'custom' : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      const gradients = {
                        'blue-purple': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        'pink-red': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        'blue-cyan': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                        'green-mint': 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
                      }
                      handleBackgroundChange('gradient', gradients[e.target.value])
                    }
                  }}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded appearance-none bg-white"
                >
                  <option value="">None</option>
                  <option value="blue-purple">Blue Purple</option>
                  <option value="pink-red">Pink Red</option>
                  <option value="blue-cyan">Blue Cyan</option>
                  <option value="green-mint">Green Mint</option>
                </select>
              </div>

              {/* Gradient Color Controls */}
              {slide?.background?.type === 'gradient' && (
                <div className="space-y-2">
                  <label className="block text-xs text-gray-600">Gradient Colors</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <input
                        type="color"
                        value={parseGradient(slide.background.value).color1}
                        onChange={(e) => {
                          const { color2 } = parseGradient(slide.background.value)
                          updateGradient(e.target.value, color2)
                        }}
                        className="w-full h-8 rounded border border-gray-300"
                      />
                      <span className="text-xs text-gray-500">Color 1</span>
                    </div>
                    <div>
                      <input
                        type="color"
                        value={parseGradient(slide.background.value).color2}
                        onChange={(e) => {
                          const { color1 } = parseGradient(slide.background.value)
                          updateGradient(color1, e.target.value)
                        }}
                        className="w-full h-8 rounded border border-gray-300"
                      />
                      <span className="text-xs text-gray-500">Color 2</span>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs text-gray-600 mb-1">Background Image</label>
                <div className="space-y-2">
                  <button
                    onClick={() => setShowBackgroundImageLibrary(true)}
                    className="w-full px-3 py-2 border border-blue-300 bg-blue-50 rounded-lg text-sm text-blue-700 hover:bg-blue-100 flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiFolder} className="w-4 h-4" />
                    <span>Select from Library</span>
                  </button>
                  <button
                    onClick={() => backgroundFileInputRef.current?.click()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiUpload} className="w-4 h-4" />
                    <span>Upload Image</span>
                  </button>
                </div>
                <input
                  ref={backgroundFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleBackgroundImageUpload}
                  className="hidden"
                />
              </div>

              {/* Background Image Position & Size Controls */}
              {slide?.background?.type === 'image' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Image Position</label>
                    <select
                      value={slide.background?.position || 'center'}
                      onChange={(e) => handleBackgroundChange('image', slide.background?.url, {
                        ...slide.background,
                        position: e.target.value
                      })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded appearance-none bg-white"
                    >
                      <option value="center">Center</option>
                      <option value="top">Top</option>
                      <option value="bottom">Bottom</option>
                      <option value="left">Left</option>
                      <option value="right">Right</option>
                      <option value="top left">Top Left</option>
                      <option value="top right">Top Right</option>
                      <option value="bottom left">Bottom Left</option>
                      <option value="bottom right">Bottom Right</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Image Size</label>
                    <select
                      value={slide.background?.size || 'cover'}
                      onChange={(e) => handleBackgroundChange('image', slide.background?.url, {
                        ...slide.background,
                        size: e.target.value
                      })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded appearance-none bg-white"
                    >
                      <option value="cover">Cover (fill slide)</option>
                      <option value="contain">Contain (fit within)</option>
                      <option value="auto">Original Size</option>
                      <option value="100% 100%">Stretch to Fit</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Only show element properties when an element is selected (not background) */}
        {element && (
          <>
            {/* Text Properties */}
            {element.type === 'text' && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-900">Text Content</h4>
                <textarea
                  value={element.content || ''}
                  onChange={(e) => handleElementUpdate({ content: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                  rows="3"
                  placeholder="Enter text content..."
                />
              </div>
            )}

            {/* List Properties */}
            {element.type === 'list' && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-900">List Items</h4>
                <div className="space-y-2">
                  {(element.content || []).map((item, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => {
                          const newContent = [...(element.content || [])]
                          newContent[index] = e.target.value
                          handleElementUpdate({ content: newContent })
                        }}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder={`Item ${index + 1}`}
                      />
                      <button
                        onClick={() => {
                          const newContent = element.content.filter((_, i) => i !== index)
                          handleElementUpdate({ content: newContent })
                        }}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newContent = [...(element.content || []), `Item ${(element.content || []).length + 1}`]
                      handleElementUpdate({ content: newContent })
                    }}
                    className="w-full px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                  >
                    + Add Item
                  </button>
                </div>
              </div>
            )}

            {/* Image Properties */}
            {element.type === 'image' && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-900">Image Source</h4>
                <div className="space-y-2">
                  <button
                    onClick={() => setShowImageGenerationModal(true)}
                    className="w-full px-3 py-2 border border-purple-300 bg-purple-50 rounded-lg text-sm text-purple-700 hover:bg-purple-100 flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiImage} className="w-4 h-4" />
                    <span>Generate AI Image</span>
                  </button>
                  <button
                    onClick={() => setShowImageLibrary(true)}
                    className="w-full px-3 py-2 border border-blue-300 bg-blue-50 rounded-lg text-sm text-blue-700 hover:bg-blue-100 flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiFolder} className="w-4 h-4" />
                    <span>Select from Library</span>
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiUpload} className="w-4 h-4" />
                    <span>Upload Image</span>
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
            )}

            {/* Video Properties */}
            {element.type === 'video' && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-900">Video URL</h4>
                <input
                  type="url"
                  value={element.videoUrl || ''}
                  onChange={(e) => handleElementUpdate({ videoUrl: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="https://youtube.com/watch?v=..."
                />
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={element.autoplay || false}
                    onChange={(e) => handleElementUpdate({ autoplay: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Autoplay</span>
                </label>
              </div>
            )}

            {/* Shape Properties */}
            {element.type === 'shape' && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-900">Shape Type</h4>
                <div className="grid grid-cols-3 gap-2">
                  {shapeOptions.map((shape) => (
                    <button
                      key={shape.id}
                      onClick={() => handleShapeChange(shape.id)}
                      className={`p-2 border rounded-lg text-xs flex flex-col items-center space-y-1 ${
                        element.style?.shapeType === shape.id 
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <SafeIcon icon={shape.icon} className="w-4 h-4" />
                      <span>{shape.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Style Properties - Common for all elements */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-900">Style</h4>
              
              {(element.type === 'text' || element.type === 'list') && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Font Size</label>
                      <input
                        type="number"
                        value={parseInt(element.style.fontSize) || 18}
                        onChange={(e) => handleStyleUpdate({ fontSize: `${e.target.value}px` })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded appearance-none bg-white"
                        min="8"
                        max="200"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Text Color</label>
                      <input
                        type="color"
                        value={element.style.color || '#374151'}
                        onChange={(e) => handleStyleUpdate({ color: e.target.value })}
                        className="w-full h-8 rounded border border-gray-300"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Text Alignment</label>
                    <select
                      value={element.style.textAlign || 'left'}
                      onChange={(e) => handleStyleUpdate({ textAlign: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded appearance-none bg-white"
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                      <option value="justify">Justify</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={element.style.fontWeight === 'bold'}
                        onChange={(e) => handleStyleUpdate({ fontWeight: e.target.checked ? 'bold' : 'normal' })}
                        className="rounded border-gray-300"
                      />
                      <span className="text-xs text-gray-700">Bold</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={element.style.fontStyle === 'italic'}
                        onChange={(e) => handleStyleUpdate({ fontStyle: e.target.checked ? 'italic' : 'normal' })}
                        className="rounded border-gray-300"
                      />
                      <span className="text-xs text-gray-700">Italic</span>
                    </label>
                  </div>
                </div>
              )}

              {element.type === 'shape' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Background Color</label>
                    <input
                      type="color"
                      value={element.style.backgroundColor || '#3b82f6'}
                      onChange={(e) => handleStyleUpdate({ backgroundColor: e.target.value })}
                      className="w-full h-8 rounded border border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Border Radius</label>
                    <input
                      type="range"
                      min="0"
                      max="50"
                      value={parseInt(element.style.borderRadius) || 0}
                      onChange={(e) => handleStyleUpdate({ borderRadius: `${e.target.value}px` })}
                      className="w-full"
                    />
                    <span className="text-xs text-gray-500">{element.style.borderRadius || '0px'}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Position & Size */}
            <div className="space-y-3 pt-4 border-t border-gray-200">
              <h5 className="text-xs font-medium text-gray-700">Position & Size</h5>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">X</label>
                  <input
                    type="number"
                    value={element.style.position?.x || 0}
                    onChange={(e) => handleStyleUpdate({ 
                      position: { ...element.style.position, x: parseInt(e.target.value) }
                    })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded appearance-none bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Y</label>
                  <input
                    type="number"
                    value={element.style.position?.y || 0}
                    onChange={(e) => handleStyleUpdate({ 
                      position: { ...element.style.position, y: parseInt(e.target.value) }
                    })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded appearance-none bg-white"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Width</label>
                  <input
                    type="number"
                    value={element.style.width || 0}
                    onChange={(e) => handleStyleUpdate({ width: parseInt(e.target.value) })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded appearance-none bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Height</label>
                  <input
                    type="number"
                    value={element.style.height || 0}
                    onChange={(e) => handleStyleUpdate({ height: parseInt(e.target.value) })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded appearance-none bg-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs text-gray-600 mb-1">Rotation (degrees)</label>
                <input
                  type="range"
                  min="-180"
                  max="180"
                  value={element.style.rotation || 0}
                  onChange={(e) => handleStyleUpdate({ rotation: parseInt(e.target.value) })}
                  className="w-full"
                />
                <span className="text-xs text-gray-500">{element.style.rotation || 0}°</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>

    {/* Image Generation Modal */}
    <ImageGenerationModal
      isOpen={showImageGenerationModal}
      onClose={() => setShowImageGenerationModal(false)}
      onImageGenerated={handleGeneratedImage}
      projectTitle={projectTitle}
      genre={genre}
      elementType={getImageElementType()}
    />

    {/* Image Library Modal */}
    <ImageLibraryModal
      isOpen={showImageLibrary}
      onClose={() => setShowImageLibrary(false)}
      onImageSelected={handleImageSelected}
      projectTitle={projectTitle}
      genre={genre}
      elementType={getImageElementType()}
    />

    {/* Background Image Library Modal */}
    <ImageLibraryModal
      isOpen={showBackgroundImageLibrary}
      onClose={() => setShowBackgroundImageLibrary(false)}
      onImageSelected={handleBackgroundImageSelected}
      projectTitle={projectTitle}
      genre={genre}
      elementType="background"
    />
    </>
  )
}

export default PropertiesPanel